/**
 * MUI TextField'ın birebir yerine geçen, büyük formlarda yazma gecikmesini gideren sürüm.
 *
 * Sorun: Quoting formları binlerce elemandan oluşan tek bir bileşen; kontrollü bir alana
 * yazılan her harf tüm formu yeniden çiziyor (~40-50 ms/tuş).
 *
 * Çözüm: Metin / sayı alanlarında yazılan değer önce alanın kendi state'inde tutulur;
 * forma (üst bileşenin onChange'i) kullanıcı yazmaya ara verince (COMMIT_DELAY) veya alandan
 * çıkınca (blur) bir kez iletilir. Kaydet butonuna tıklamak blur'u tetiklediği için son değer
 * her zaman kayda girer.
 *
 * Tamponlanmayan (doğrudan MUI TextField'a geçen) durumlar:
 *  - select alanları
 *  - Autocomplete / DatePicker renderInput'ları (value/onChange doğrudan verilmez)
 *  - value veya onChange verilmemiş (kontrolsüz) alanlar
 *  - metin/sayı dışındaki tipler (date, file, ...)
 *  - klavye işleyicisi (onKeyDown / onKeyPress / onKeyUp) olan alanlar
 */
import React, { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import TextField from "@mui/material/TextField";

const COMMIT_DELAY = 250;
const BUFFERED_TYPES = new Set([undefined, "", "text", "number", "search", "email", "tel", "url"]);

const toDisplay = (value) => (value === null || value === undefined ? "" : value);

const BufferedTextField = forwardRef(function BufferedTextField(props, ref) {
    const { value, onChange, onBlur, select, type, onKeyDown, onKeyPress, onKeyUp } = props;
    // Klavye işleyicisi olan alanlar (Enter ile arama/ekleme vb.) form state'ini anında okur;
    // tampon o anda henüz iletilmemiş olabileceği için bunlar doğrudan çalışır
    const hasKeyHandler = Boolean(onKeyDown || onKeyPress || onKeyUp);
    const shouldBuffer =
        !select &&
        !hasKeyHandler &&
        typeof onChange === "function" &&
        value !== undefined &&
        BUFFERED_TYPES.has(type);

    // Hook sırası sabit kalsın diye tampon her zaman kurulur; gerekmiyorsa kullanılmaz
    const [local, setLocal] = useState(toDisplay(value));
    const dirtyRef = useRef(false);
    const timerRef = useRef(null);
    const pendingEventRef = useRef(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const propValueRef = useRef(value);
    propValueRef.current = value;
    const onBlurRef = useRef(onBlur);
    onBlurRef.current = onBlur;

    // Dışarıdan gelen değer değişince (hesaplanan alanlar, düzenleme yüklemesi, sıfırlama)
    // kullanıcının henüz iletilmemiş bir yazısı yoksa alanı güncelle
    useEffect(() => {
        if (!dirtyRef.current) setLocal(toDisplay(value));
    }, [value]);

    const flush = useCallback(() => {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        if (!dirtyRef.current || !pendingEventRef.current) return false;
        const evt = pendingEventRef.current;
        pendingEventRef.current = null;
        dirtyRef.current = false;
        onChangeRef.current?.(evt);
        // Üst bileşen değeri reddettiyse / dönüştürdüyse (ör. negatif sayı engeli) onun değerine dön
        setTimeout(() => {
            if (!dirtyRef.current) setLocal(toDisplay(propValueRef.current));
        }, 0);
        return true;
    }, []);

    // Bileşen kaldırılırken bekleyen değeri kaybetme
    useEffect(() => () => flush(), [flush]);

    if (!shouldBuffer) return <TextField ref={ref} {...props} />;

    const handleChange = (e) => {
        const next = e.target.value;
        setLocal(next);
        dirtyRef.current = true;
        // React event havuzu kullanmıyor ama hedef eleman sonra değişebilir; değeri sabitle
        const target = { value: next, name: e.target.name, type: e.target.type, id: e.target.id };
        pendingEventRef.current = {
            target,
            currentTarget: target,
            type: "change",
            nativeEvent: e.nativeEvent,
            persist() {},
            preventDefault() {},
            stopPropagation() {},
        };
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(flush, COMMIT_DELAY);
    };

    const handleBlur = (e) => {
        // Bekleyen değer iletildiyse üst bileşenin onBlur işleyicisi (ör. vergi no ile sorgu)
        // form güncellendikten sonra, güncel haliyle çalışsın; aksi halde eski değeri okurdu
        if (flush()) setTimeout(() => onBlurRef.current?.(e), 0);
        else onBlur?.(e);
    };

    return <TextField ref={ref} {...props} value={local} onChange={handleChange} onBlur={handleBlur} />;
});

export default BufferedTextField;
